import { useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { useParams } from 'react-router';
import ScrollbarList from 'components/Common/ScrollbarList';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import WaitingAnimationScreen from 'components/Dashboard/StakeHolders/WaitingAnimationScreen';
import { createCalendarRenderElements } from 'components/Dashboard/UpcomingEvents/CalendarSearchRenderFunctions';
import EmptyMessage from 'components/Dashboard/components/EmptyMessage';
import ResearchPortalButton from 'components/Dashboard/components/ResearchPortalButton';
import { sectionName as rawSectionName, createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import ParliamentLiveItem from 'components/Dashboard/ParliamentLive/ParliamentLiveItem';
import EventsListEmpty from 'components/Dashboard/LiveFromParliament/EventsListEmpty';
import useCreateParliamentLiveFilters from 'components/Dashboard/ParliamentLive/useCreateParliamentLiveFilters';
import { useSiteSectionsNames } from 'utils/getAppSite';
import ListItemSkeleton from 'components/Dashboard/ListControl/ListItemSkeleton';
import ListItem from 'components/Dashboard/ListControl/ListItem';
import ListSkeleton from 'components/Dashboard/ListControl/ListSkeleton';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';
import RatingUpdateBadge from 'components/Dashboard/PriorityUpdates/RatingUpdateBadge';

const GeneralList = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults, setContentResults } = useContentResults();
  const { sectionsView, realtimeEvents } = state;
  const { selectedOptionParliamentLiveView } = sectionsView ?? {};
  const { isLoading, createResults, currentlyProcessing, elements, section, scrollBarRef, activeUpdateState } = props;

  const { activeUpdate, setActiveUpdate } = activeUpdateState ?? {};
  const sectionName = createSectionName();
  const params = useParams();
  const { inboxSection } = useMainSectionParameter(params);
  const [showItems, setShowItems] = useState(false);
  const [freshItemsHeight, setFreshItemsHeight] = useState(0);
  const freshItemsRef = useRef(null);

  const { realtimeEventsHaveNotStarted } = useCreateParliamentLiveFilters();
  const { getName } = useSiteSectionsNames();
  const { rateUpdatesMode } = useDetectRateMode();

  const freshElements = elements.filter((item) => item.freshItem === true);
  const transitionDuration = freshElements.length >= 15 ? 1000 : 500;
  //AE: The idea is to test only showing 100 results always to avoid have a lot of result on screen;
  const elementsToRender =
    sectionName === 'ParliamentLive' ? elements?.filter((item) => !item?.freshItem)?.slice(0, 100) : elements;

  const showNewItems = () => {
    setShowItems(true);
  };

  useEffect(() => {
    if (freshItemsRef.current) {
      setFreshItemsHeight(freshItemsRef.current.getBoundingClientRect().height);
    }
  }, [elements.length, freshItemsRef.current, showItems]);

  useEffect(() => {
    //AE: The idea with this change is to avoid having rendered elements on screen if the user has not clicked yet on the show button.
    setTimeout(() => {
      if (!!showItems && sectionName === 'ParliamentLive') {
        if (freshItemsRef.current) {
          freshItemsRef.current.classList.add('scrolled');
          setFreshItemsHeight(0);
          setTimeout(() => {
            let sectionWithoutScroll = { ...section, hits: elements.map((item) => ({ ...item, freshItem: false })) };
            setContentResults({
              ...contentResults,
              [sectionName]: sectionWithoutScroll,
            });
            freshItemsRef.current.classList.remove('scrolled');
            setShowItems(false);
          }, transitionDuration);
        }
      }
    }, 300);
  }, [showItems]);

  const createEmptyMessage = () => {
    switch (rawSectionName()) {
      case 'ParliamentaryRecord':
      case 'KeyUpdates':
        return 'updates';
      case 'ParliamentLive':
        return 'parliamentary proceedings';
      case 'Tweets':
        return 'X posts';
      default:
        return getName({ property: TransformFromPascalCase(rawSectionName())?.toLowerCase() });
    }
  };

  return (
    <div className={`col-16 px-0`}>
      {isLoading && <ListSkeleton sectionName={sectionName} />}
      {!isLoading && (
        <>
          {sectionName === 'ParliamentLive' && (
            <button
              className={`fresh-items-button general-button action-button rounded-button alerts-button z-max ${
                freshElements.length > 0 ? '' : 'fresh-items-button-hidden'
              }`}
              onClick={showNewItems}
            >
              <span className='icon-arrow-top mr-2'></span>New{' '}
              {selectedOptionParliamentLiveView === 'Relevant remarks' ? 'remarks' : 'events'} ({freshElements.length})
            </button>
          )}
          {currentlyProcessing.state === false && inboxSection ? (
            <WaitingAnimationScreen section={section} />
          ) : (
            <>
              {section?.loaded && (
                <>
                  {elements.length === 0 ? (
                    <>
                      {sectionName === 'ParliamentLive' &&
                      (realtimeEvents?.length === 0 || realtimeEventsHaveNotStarted()) ? (
                        <div className='empty-main-container'>
                          <EventsListEmpty todayEvents={realtimeEvents?.length > 0 && realtimeEventsHaveNotStarted()} />
                        </div>
                      ) : (
                        <EmptyMessage
                          commonMessageProp={
                            sectionName === 'ParliamentLive'
                              ? realtimeEvents?.some((event) => !event?.eventHasEnded)
                                ? 'Parliament is live but there are no relevant remarks to show you'
                                : 'No live parliamentary proceedings found'
                              : null
                          }
                          resourceName={createEmptyMessage()}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <ScrollbarList ref={scrollBarRef} createResults={createResults}>
                        {sectionName === 'UpcomingEvents' ? (
                          <div className='py-2'>
                            {rateUpdatesMode ? (
                              <div className='items-list'>
                                {elements?.map((item, index) => {
                                  const importanceRating =
                                    contentResults['UpcomingEvents']?.feedItemFlags?.[index]?.importanceRating;
                                  return (
                                    <div
                                      className={`pointer rounded rate-updates-content-item ${activeUpdate?.objectID === item?.objectID ? 'rate-update-item-selected' : ''} ${index === 0 ? 'mt-0' : ''}`}
                                      key={`item-${normalize(sectionName)}-${index}`}
                                      onClick={() => {
                                        setActiveUpdate({ ...item, importanceRating });
                                      }}
                                    >
                                      <li className={`content-item `}>
                                        <div className='py-3 px-4'>
                                          <div className='d-flex align-items-baseline mb-2 ml-2'>
                                            <h4 className={`mb-0 flex-grow-1 content-item-title`}>{item.title}</h4>
                                          </div>
                                          <div className='specific-left-padding-7 mt-2'>
                                            {contentResults['UpcomingEvents']?.feedItemFlags?.[index]
                                              ?.importanceRating && (
                                              <RatingUpdateBadge
                                                item={{
                                                  rating: TransformFromPascalCase(importanceRating),
                                                }}
                                              />
                                            )}
                                          </div>
                                        </div>
                                      </li>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <>{createCalendarRenderElements({ elements, feedItemFlags: section?.feedItemFlags })}</>
                            )}
                          </div>
                        ) : (
                          <>
                            {sectionName === 'ParliamentLive' && showItems && (
                              <ul
                                ref={freshItemsRef}
                                className='policy-list px-0 items-list all-open-consultations fresh-items-list mb-0'
                                style={{
                                  marginTop: `-${freshItemsHeight}px`,
                                  transition: `margin-top ${transitionDuration}ms ease-out`,
                                }}
                              >
                                {elements
                                  ?.filter((item) => item?.freshItem)
                                  ?.slice(0, 100)
                                  .map((item, index, array) => {
                                    return (
                                      <ParliamentLiveItem
                                        key={`item-${normalize(sectionName)}-${index}`}
                                        listRow={{
                                          listContext: {
                                            item,
                                            array,
                                            index,
                                            scrollBarRef,
                                            listView: true,
                                          },
                                        }}
                                      />
                                    );
                                  })}
                              </ul>
                            )}

                            <ul
                              className={`policy-list px-0 items-list pb-2 all-open-consultations ${rateUpdatesMode ? 'rate-updates-list-container' : ''}`}
                            >
                              {elementsToRender
                                ?.filter((item) => !item?.freshItem)
                                .map((item, index, array) => {
                                  const listRow = {
                                    listContext: {
                                      item,
                                      array,
                                      index,
                                      scrollBarRef,
                                      listView: true,
                                      openInNewTab: sectionName === 'ElectionKeyUpdates',
                                    },
                                    feedOverrides: {},
                                    display: {},
                                    interactions: { showPriorityBadge: true, setActiveUpdate },
                                    external: {},
                                    sectionMeta: {},
                                  };
                                  return (
                                    <div
                                      className={`${rateUpdatesMode ? `rounded rate-updates-content-item ${activeUpdate?.objectID === item?.objectID ? 'rate-update-item-selected' : ''}` : ''} `}
                                      key={`item-${normalize(sectionName)}-${index}`}
                                    >
                                      <ListItem
                                        sectionName={sectionName}
                                        index={index}
                                        listRow={listRow}
                                        item={item}
                                        section={section}
                                      />
                                    </div>
                                  );
                                })}
                              {section?.thereIsMoreContent && sectionName !== 'ParliamentLive' && (
                                <ListItemSkeleton sectionName={sectionName} />
                              )}
                            </ul>
                          </>
                        )}
                        <ResearchPortalButton />
                      </ScrollbarList>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GeneralList;
